import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import { type RefObject, useCallback } from "react";
import { getThreadName } from "@/components/threads/util.ts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import { confirmationToast, errorToast, infoToast } from "@/components/ui/toast.tsx";
import { useDeleteThread } from "@/hooks/useThreads.tsx";
import type { GetThreadResp } from "@/types/thread.ts";

/**
 * The one confirmation a thread deletion goes through, whichever surface starts
 * it. The detail panel's menu and a list row's both open this, so the copy and
 * the already-gone handling cannot drift apart between them.
 *
 * `onDeleted` is the only thing the surfaces disagree about: the panel has to
 * leave the thread it was showing, while a row simply stops being rendered.
 */
export const ThreadDeleteDialog = ({
  thread,
  open,
  onOpenChange,
  onDeleted,
  restoreFocusRef,
  statsigName,
}: {
  thread: GetThreadResp;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Runs after a confirmed delete, for hosts that must navigate away from it. */
  onDeleted?: () => void;
  /**
   * Focus lands here on close. The control that opened the dialog is usually a
   * menu item that unmounted with its menu, leaving Radix nothing to restore.
   */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  statsigName: string;
}) => {
  const deleteMutation = useDeleteThread({ threadId: thread.id });
  const { mutateAsync } = deleteMutation;
  const threadId = thread.id;

  const handleDelete = useCallback(async () => {
    try {
      await mutateAsync();
    } catch (error) {
      // A thread that is already gone — deleted from another tab, or a retry
      // after the response was lost — is the outcome the user asked for, so it
      // takes the success path. `verifyThreadOwnership` answers 403 for a thread
      // that no longer exists, which is why both codes count here.
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status !== 403 && status !== 404) {
        Sentry.captureException(error, {
          tags: { component: "ThreadDeleteDialog", operation: "deleteThread" },
          extra: { threadId },
        });
        errorToast("Failed to delete thread");
        // Leave the user where they are rather than acting on a failure.
        return;
      }
    }
    onOpenChange(false);
    confirmationToast("Thread deleted");
    onDeleted?.();
  }, [mutateAsync, threadId, onOpenChange, onDeleted]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        // Radix portals this to the document root, but a React portal bubbles
        // its events through the React tree — so on a host that sits inside a
        // link (a thread row), Cancel's click reached that link and opened the
        // very thread the user had just decided not to delete.
        onClick={(e) => e.stopPropagation()}
        // Escape bypasses the buttons, so without this it dismisses the dialog
        // mid-delete while Cancel is sitting there disabled.
        onEscapeKeyDown={(e) => {
          if (deleteMutation.isPending) e.preventDefault();
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          restoreFocusRef?.current?.focus();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this thread?</AlertDialogTitle>
          {/* "Removed" rather than "permanently deleted": the thread is
              soft-deleted, so support can still recover it. Saying "permanent"
              would be a promise the backend does not keep. From the user's
              side it is still one-way, which is what "can't undo this" says.
              Memories are called out because they are the part a user might
              expect to lose and would miss. */}
          <AlertDialogDescription>
            “{getThreadName(thread)}” and its messages will be removed from Okthx. You can't undo
            this. Anything Okthx remembered from this conversation is kept.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={deleteMutation.isPending}
            // Only the explicit button confirms the cancel. Escape and an
            // outside click are a change of mind mid-gesture, not an action
            // the user needs told back to them.
            onClick={() => infoToast("Delete cancelled")}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // Radix closes the dialog on action-select. Deleting is async and
              // can fail, and the error path leaves the user in place — so the
              // close is deferred to `handleDelete`'s success branch.
              e.preventDefault();
              void handleDelete();
            }}
            disabled={deleteMutation.isPending}
            // The button's own variant, not hand-rolled classes: `asChild`
            // merges the variant's styling onto the child, so a bare
            // `text-white` here lands on the default variant's light fill and
            // the label disappears until hover.
            variant="destructive"
            data-statsig-name={statsigName}
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete thread"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
